import { Typography, Button, Toolbar, Tooltip } from "@mui/material";
import { CSVLink } from "react-csv";
import { FileDownload } from "@mui/icons-material";
import { useEffect, useMemo } from "react";
import { getSummaryCount, normalizeDeviceCounts } from "./utils";
import { formatCSVData } from "./tsutils";
import MinuteReport from "./MinuteReport";

import { useUserContext } from "../../../providers/useUserProvider";
import { CountType, cumulativeSum } from "../../../types/Count";
import { useFetchDeviceVideos } from "../../../hooks/useFetchDeviceVideos";
import { appDefaultTz } from "../../../providers/ApiClientProvider";
import { DeviceSummaryLayout } from "../../../features/device-summary";
import {
    toTemporalZonedDateTime,
    startOf,
    formatDate,
} from "../../../shared/lib/temporal";

export const EMPTY_DATA_STRING = "-";

const createPlaceholderCount = (timestamp) => {
    return {
        timestamp: timestamp,
        instant: {
            Upstream: { Predicted: EMPTY_DATA_STRING },
            Downstream: { Predicted: EMPTY_DATA_STRING },
            Net: { Predicted: EMPTY_DATA_STRING },
        },
        cumulative: {
            Upstream: { Predicted: EMPTY_DATA_STRING },
            Downstream: { Predicted: EMPTY_DATA_STRING },
            Net: { Predicted: EMPTY_DATA_STRING },
        },
        isPlaceholder: true,
    };
};

const DailyReport = ({
    setErrorState,
    devices,
    deviceCounts,
    date,
    deployment,
}) => {
    const { selectedSite: site, timezoneDetails } = useUserContext();
    const timezone = timezoneDetails?.timezone || appDefaultTz;

    const {
        data: videosByDevice,
        isError: hasVideoError,
        error: videoError,
    } = useFetchDeviceVideos({
        devices: devices || [],
        date,
        timezone,
        enabled: !!devices && !!date && !!timezone,
    });

    const filterDataWithVideos = (data) => {
        const dailyData = {};

        Object.entries(data).forEach(([key, deviceCount]) => {
            dailyData[key] = {
                device: deviceCount.device,
                counts: normalizeDeviceCounts(deviceCount, date, timezone),
            };
        });

        if (videosByDevice) {
            Object.entries(dailyData).forEach(([key, deviceCount]) => {
                const deviceId = deviceCount.device.id;
                const hourlyVideos = videosByDevice[deviceId] || {};

                const existingHours = new Set(
                    deviceCount.counts.map((count) => {
                        const temporal = toTemporalZonedDateTime(
                            count.timestamp,
                            timezone,
                        );
                        return formatDate(
                            startOf(temporal, "hour"),
                            "YYYY-MM-DD HH:mm",
                        );
                    }),
                );

                Object.keys(hourlyVideos).forEach((hourKey) => {
                    if (!existingHours.has(hourKey)) {
                        const placeholderCount =
                            createPlaceholderCount(hourKey);
                        if (
                            deviceCount.device.configs?.[0]?.type ===
                            "UNIDIRECTIONAL"
                        ) {
                            placeholderCount.instant = {
                                Total: EMPTY_DATA_STRING,
                            };
                            placeholderCount.cumulative = {
                                Total: EMPTY_DATA_STRING,
                            };
                        }

                        deviceCount.counts.push(placeholderCount);
                    }
                });

                deviceCount.counts.sort((a, b) => {
                    const aTime =
                        a.timestamp instanceof Date
                            ? a.timestamp.getTime()
                            : new Date(a.timestamp).getTime();
                    const bTime =
                        b.timestamp instanceof Date
                            ? b.timestamp.getTime()
                            : new Date(b.timestamp).getTime();
                    return aTime - bTime;
                });
            });
        }

        Object.entries(dailyData).forEach(([key, deviceCount]) => {
            const realCounts = deviceCount.counts.filter(
                (count) => !count.isPlaceholder,
            );
            if (realCounts.length > 0) {
                const cumulatives = cumulativeSum(realCounts);
                let cumulativeIndex = 0;

                deviceCount.counts.forEach((count, index) => {
                    if (!count.isPlaceholder) {
                        dailyData[key].counts[index].cumulative =
                            cumulatives[cumulativeIndex].cumulative;
                        cumulativeIndex++;
                    }
                });
            }
        });

        return dailyData;
    };

    const data = useMemo(
        () => filterDataWithVideos(deviceCounts),
        [date, deviceCounts, videosByDevice],
    );

    useEffect(() => {
        if (hasVideoError && videoError) {
            console.error("Error loading video data:", videoError);
            setErrorState({
                hasError: true,
                message: "Error loading video data, showing count data only",
            });
        }
    }, [hasVideoError, videoError, setErrorState]);

    const csvDisabled = useMemo(
        () =>
            data == undefined ||
            ![...Object.values(data)].some((deviceCounts) =>
                deviceCounts.counts.some((count) => !count.isPlaceholder),
            ),
        [data],
    );

    const summaryCounts = useMemo(() => {
        return Object.values(data).map((deviceCounts) => ({
            ...getSummaryCount(deviceCounts),
            counts: deviceCounts.counts,
        }));
    }, [data]);

    return (
        <>
            <DeviceSummaryLayout
                date={date}
                summaries={summaryCounts}
                timezone={timezone}
            />

            <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                <Typography
                    sx={{ flex: "1 1 100%", color: "#043c4a" }}
                    variant="h4"
                    id="tableTitle"
                    component="div"
                >
                    Hourly Report
                </Typography>
                <CSVLink
                    data={formatCSVData(
                        Object.fromEntries(
                            Object.entries(data).map(([key, deviceCount]) => [
                                key,
                                {
                                    ...deviceCount,
                                    counts: deviceCount.counts.filter(
                                        (count) => !count.isPlaceholder,
                                    ),
                                },
                            ]),
                        ),
                        CountType.Predicted,
                        timezone,
                        "hour",
                    )}
                    filename={`${site.name}_hourly_report_${
                        new Date(date).toISOString().split("T")[0]
                    }.csv`}
                    onClick={() => {
                        return !csvDisabled;
                    }}
                    target="_blank"
                >
                    <Tooltip title="Export Daily Hourly Summary (CSV)">
                        <span>
                            <Button
                                sx={{
                                    background: "#4eafb2",
                                    "&:hover": {
                                        backgroundColor: "#043c4a",
                                        boxShadow: "none",
                                    },
                                }}
                                startIcon={<FileDownload />}
                                disabled={csvDisabled}
                                variant="contained"
                            >
                                Export
                            </Button>
                        </span>
                    </Tooltip>
                </CSVLink>
            </Toolbar>

            <MinuteReport
                site={site}
                devices={devices}
                deviceCounts={data}
                selectedDate={date}
                deploymentStartTime={deployment?.startTime}
                deploymentEndTime={deployment?.endTime}
                setErrorState={setErrorState}
            />
        </>
    );
};

export default DailyReport;
